import { Tag } from 'antd';
import styled from 'styled-components';
import { BRAND } from '@/constants/brand';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

/**
 * Status tag palette (dark shell: rows sit on BRAND.ink).
 * Each variant pairs a translucent tint background with an AA-compliant text
 * colour — contrast ratios below are text-vs-ink, verified against WCAG AA's
 * 4.5:1 minimum for body text:
 *   draft     rgba(255,255,255,0.72) on ink  ->  9.86:1
 *   sent      #8888e1 (lavanda + 25% white) on ink -> 6.00:1
 *   viewed    ${BRAND.hacienda} on ink        -> 10.37:1
 *   completed #52c41a on ink                  ->  8.35:1
 *   error     ${BRAND.casa} on ink            ->  6.60:1
 *   superseded #a99ac9 on ink                 ->  7.35:1
 *   awaiting-order #ff9d4d on ink             ->  9.18:1
 *   client-order   #5bc8a8 on ink             ->  9.26:1
 * Draft is intentionally neutral (no colour) since it is the default,
 * inactive state and shouldn't compete for attention with the others.
 *
 * Light mode keeps the same tints but darkens each text colour, since the
 * dark-mode ones fall to 1.7–2.6:1 on white. Text vs its tint over white:
 *   sent 6.18, viewed 5.47, completed 4.99, error 5.72, superseded 5.67,
 *   awaiting-order 4.91, client-order 5.52
 */
export const StatusTag = styled(Tag)`
  border-radius: 4px;
  font-weight: 500;
  text-transform: capitalize;

  &.status-draft {
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.08);
    border-color: rgba(${APP_SHELL_THEME.overlayRgb}, 0.2);
    color: rgba(${APP_SHELL_THEME.overlayRgb}, 0.72);
  }

  &.status-sent {
    background: ${BRAND.lavanda_10};
    border-color: rgba(97, 97, 215, 0.5);
    color: #8888e1;
  }

  &.status-viewed {
    background: rgba(243, 181, 71, 0.14);
    border-color: rgba(243, 181, 71, 0.45);
    color: ${BRAND.hacienda};
  }

  &.status-completed {
    background: rgba(82, 196, 26, 0.14);
    border-color: rgba(82, 196, 26, 0.45);
    color: #52c41a;
  }

  &.status-error {
    background: ${BRAND.casa_10};
    border-color: rgba(246, 109, 107, 0.5);
    color: ${BRAND.casa};
  }

  /* A replaced IO is finished business, not a failure: muted violet reads as
     retired without the alarm of the error red, and stays clearly apart from
     the green a signed document keeps at the provider. */
  &.status-superseded {
    background: rgba(169, 154, 201, 0.14);
    border-color: rgba(169, 154, 201, 0.45);
    color: #a99ac9;
  }

  /* Sits next to the status rather than replacing it: the document is signed,
     and what needs flagging is that its booking has not followed. Orange
     rather than the viewed amber so the two do not read as the same state. */
  &.status-awaiting-order {
    background: rgba(255, 157, 77, 0.14);
    border-color: rgba(255, 157, 77, 0.5);
    color: #ff9d4d;
  }

  /* Confirmed by an order we signed. Teal rather than the green a signed IO
     uses: the booking is confirmed either way, but by a different document,
     and collapsing the two would lose the distinction the tag exists for. */
  &.status-client-order {
    background: rgba(91, 200, 168, 0.14);
    border-color: rgba(91, 200, 168, 0.45);
    color: #5bc8a8;
  }

  html[data-theme='light'] && {
    &.status-sent {
      color: ${APP_SHELL_THEME.accentText};
    }
    &.status-viewed {
      color: #8a5a00;
    }
    &.status-completed {
      color: #237804;
    }
    &.status-error {
      color: #b42318;
    }
    &.status-superseded {
      color: #6a5689;
    }
    &.status-awaiting-order {
      color: #ad4e00;
    }
    &.status-client-order {
      color: #006d75;
    }
  }
`;

/** Status plus any flag that qualifies it, wrapping on a narrow column. */
export const StatusTagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

export const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/**
 * Rows are clickable (open the preview drawer), so every data cell gets a pointer
 * cursor. The background-color transition on hover is the only motion added here;
 * it is short and respects prefers-reduced-motion rather than being decorative.
 */
export const TableContainer = styled.div`
  .ant-table-tbody > tr > td {
    cursor: pointer;
  }

  .ant-table-tbody > tr > td.io-actions-cell {
    cursor: default;
  }

  @media not (prefers-reduced-motion: reduce) {
    .ant-table-tbody > tr > td {
      transition: background-color 160ms ease-out;
    }
  }
`;

/** Search + status filter row above the IO table. */
export const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  .ant-input-affix-wrapper {
    max-width: 320px;
  }

  .ant-select {
    min-width: 180px;
  }
`;
